import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Textarea } from '@mlm/ui';
import { submitCourseReview, type CourseReview } from '../../lib/api';

const STAR_LABELS = ['Terrible', 'Poor', 'OK', 'Good', 'Excellent'];

/**
 * Rate a course you're enrolled in. The server upserts on both verbs, so an
 * existing review is EDITED in place rather than rejected — which is why the
 * form seeds from `existing` instead of starting blank for a repeat visitor.
 *
 * Eligibility is the caller's business: this renders only once the learner may
 * actually post, so there is no disabled-and-unexplained state here.
 */
export function CourseReviewForm({
  courseId,
  existing,
  onSaved,
}: {
  courseId: string;
  existing: CourseReview | null;
  onSaved: () => void;
}) {
  const [rating, setRating] = useState(existing?.rating ?? 0);
  const [comment, setComment] = useState(existing?.comment ?? '');

  const save = useMutation({
    meta: { errorMessage: "Couldn't save your review — please try again." },
    mutationFn: () =>
      submitCourseReview(
        courseId,
        { rating, comment: comment.trim() || null },
        { edit: !!existing },
      ),
    onSuccess: onSaved,
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (rating > 0 && !save.isPending) save.mutate();
      }}
      className="rounded border border-[#d1d7dc] p-4"
    >
      <h3 className="text-[15px] font-bold text-[#222]">
        {existing ? 'Edit your review' : 'Rate this course'}
      </h3>
      <div className="mt-2 flex items-center gap-1" role="group" aria-label="Your rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-pressed={rating === n}
            aria-label={`${n} star${n === 1 ? '' : 's'} — ${STAR_LABELS[n - 1]}`}
            className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-o"
          >
            <Star
              className="h-7 w-7"
              fill={n <= rating ? '#FFB400' : 'none'}
              color={n <= rating ? '#FFB400' : '#6a6f73'}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-sm text-[#6a6f73]">{STAR_LABELS[rating - 1]}</span>
        )}
      </div>
      <label htmlFor="course-review-comment" className="mt-3 block text-xs text-[#6a6f73]">
        Tell other learners what this course was like (optional)
      </label>
      <Textarea
        id="course-review-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={2000}
        rows={3}
        className="mt-1"
      />
      <div className="mt-3 flex items-center gap-3">
        <button
          type="submit"
          disabled={rating === 0 || save.isPending}
          className="rounded bg-o px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-o-d disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : existing ? 'Update review' : 'Submit review'}
        </button>
        {rating === 0 && <span className="text-xs text-[#6a6f73]">Pick a star rating first.</span>}
      </div>
    </form>
  );
}
